import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { FreightAllocationEditor } from '@/components/freight/FreightAllocationEditor'
import { FreightEditorAudience } from '@/components/freight/freightEditorAudience'
import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { parseError } from '@/lib/errors'
import { dropZeroShares, MAX_FREIGHT_TYPES, normalizeShares } from '@/lib/freightAllocation'
import { type CommodityItem, updateFreightTypes, useOrgFreightSuggestions } from '../../models/user'

interface EditFreightDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  commodities: CommodityItem[]
  orgId: string
}

export function EditFreightDetailsDialog({
  open,
  onOpenChange,
  onSuccess,
  commodities,
  orgId,
}: EditFreightDetailsDialogProps) {
  const [loading, setLoading] = useState(false)
  const requote = useRequoteOnSave()
  const [items, setItems] = useState<CommodityItem[]>([])
  const { suggestions, suggestionGroups, fmcsaCargo } = useOrgFreightSuggestions(open ? orgId : undefined)
  const kept = dropZeroShares(items)

  useEffect(() => {
    if (open) setItems(normalizeShares(commodities))
  }, [open, commodities])

  async function handleSave() {
    setLoading(true)
    try {
      await updateFreightTypes(orgId, items)
      onSuccess()
      if (requote) await requote.run()
      else toast.success('Freight details updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Edit freight details</DialogTitle>
        </DialogHeader>

        <FreightAllocationEditor
          id="edit-freight"
          value={items}
          onChange={setItems}
          suggestions={suggestions}
          suggestionGroups={suggestionGroups}
          fmcsaCargo={fmcsaCargo}
          audience={FreightEditorAudience.CUSTOMER}
          disabled={loading}
        />

        <DialogFooter className="mt-6">
          <Button type="button" variant="link" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={kept.length === 0 || kept.length > MAX_FREIGHT_TYPES}
            loading={loading ? (requote?.pendingLabel ?? 'Saving...') : undefined}
          >
            {requote?.label ?? 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
